// Offline queue: persists pending mutations in localStorage and replays them
// when the browser is back online. Supports both JSON and multipart (photo).
//
// SECURITY NOTE — localStorage is intentional here:
//   * We NEVER store auth tokens (JWT lives in an HttpOnly cookie).
//   * We NEVER store PII beyond what the user just typed and is about to POST.
//   * We NEED cross-session persistence: if the tech closes the tab offline,
//     the queued photo/task upload must survive until connectivity returns.
//   sessionStorage would drop the queue on tab close (bad UX). IndexedDB is
//   overkill for the <1MB payload sizes we deal with.

const KEY = "bkpro_offline_queue";

function readQueue() {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch (e) {
    console.warn("Offline queue parse error, resetting:", e?.message || e);
    return [];
  }
}
function writeQueue(items) {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch (e) {
    // Storage full — drop oldest until it fits
    console.warn("Offline queue full, dropping oldest items:", e?.message || e);
    while (items.length > 1) {
      items.shift();
      try {
        localStorage.setItem(KEY, JSON.stringify(items));
        window.dispatchEvent(new CustomEvent("bkpro-queue-change", { detail: { size: items.length } }));
        return;
      } catch (err) {
        console.warn("Retry after drop failed:", err?.message || err);
      }
    }
  }
  window.dispatchEvent(new CustomEvent("bkpro-queue-change", { detail: { size: items.length } }));
}

export function isOnline() {
  return typeof navigator === "undefined" ? true : navigator.onLine;
}
export function queueSize() { return readQueue().length; }

export function enqueue(action) {
  const q = readQueue();
  const item = { id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, ...action, queued_at: new Date().toISOString(), tries: 0 };
  q.push(item);
  writeQueue(q);
  return item;
}

// Convert a File/Blob to base64 data URL for storage
export async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onerror = reject;
    r.onload = () => resolve(r.result);
    r.readAsDataURL(file);
  });
}

// Convert a base64 data URL back to a Blob
function dataUrlToBlob(dataUrl) {
  const [meta, b64] = dataUrl.split(",");
  const mime = /data:(.*?);base64/.exec(meta)?.[1] || "application/octet-stream";
  const bin = atob(b64);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

/**
 * Enqueue a photo upload action. Photo is stored as base64.
 */
export async function enqueuePhoto({ chantier_id, kind, file, filename }) {
  const dataUrl = await fileToBase64(file);
  return enqueue({
    type: "photo",
    method: "post",
    url: "/photos",
    data: { chantier_id, kind, filename: filename || file.name || "photo.jpg", dataUrl, content_type: file.type || "image/jpeg" },
  });
}

export async function flushQueue(api, onProgress) {
  const q = readQueue();
  if (!q.length) return { flushed: 0, failed: 0 };
  const remaining = [];
  let flushed = 0, failed = 0;
  for (const it of q) {
    try {
      if (it.type === "photo") {
        const blob = dataUrlToBlob(it.data.dataUrl);
        const fd = new FormData();
        fd.append("chantier_id", it.data.chantier_id);
        fd.append("kind", it.data.kind || "before");
        fd.append("file", blob, it.data.filename);
        await api.post(it.url, fd, { headers: { "Content-Type": "multipart/form-data" } });
      } else {
        const cfg = { headers: it.headers || undefined };
        if (it.method === "post") await api.post(it.url, it.data, cfg);
        else if (it.method === "patch") await api.patch(it.url, it.data, cfg);
        else if (it.method === "delete") await api.delete(it.url, cfg);
        else if (it.method === "put") await api.put(it.url, it.data, cfg);
      }
      flushed++;
      onProgress?.({ flushed, failed, total: q.length });
    } catch (err) {
      const status = err?.response?.status;
      if (!status || status >= 500) {
        remaining.push({ ...it, tries: (it.tries || 0) + 1 });
        failed++;
      } else {
        failed++;
      }
    }
  }
  writeQueue(remaining);
  return { flushed, failed };
}

export function clearQueue() { writeQueue([]); }

export function attachAutoFlush(api, onDone) {
  const handler = async () => {
    if (isOnline() && queueSize() > 0) {
      const r = await flushQueue(api);
      onDone?.(r);
    }
  };
  window.addEventListener("online", handler);
  return () => window.removeEventListener("online", handler);
}
